import React from 'react';
import { motion } from 'framer-motion';
import {
    Code2, Cpu, Database, Cloud, Shield, Server,
    Terminal, GitBranch, Layers, Workflow, Blocks,
    Activity, Settings, Lock, Smartphone, Globe, ChevronLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const CustomSoftware = () => {
    const offerings = [
        { title: "Enterprise", desc: "Scalable systems for large-scale operations.", icon: Layers },
        { title: "Cloud Native", desc: "Native development on AWS, Azure, or GCP.", icon: Cloud },
        { title: "SaaS Platforms", desc: "Multi-tenant subscription architectures.", icon: Blocks },
        { title: "API Systems", desc: "Robust, high-performance API engines.", icon: GitBranch },
        { title: "Modernization", desc: "Transforming legacy into high-speed assets.", icon: Settings },
        { title: "Data Engines", desc: "Optimized storage for massive workloads.", icon: Database }
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="Custom Software Development | Enterprise & SaaS Solutions - MehareTech"
                description="We engineer high-performance custom software tailored to your business needs. Specialized in enterprise architecture, SaaS platform development, and cloud-native engineering."
                keywords="custom software development, enterprise software solutions, SaaS development company, business automation software, bespoke software India, cloud native application, legacy system modernization"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-emerald-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </Link>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-emerald-600/10 blur-[120px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-emerald-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            Enterprise Engineering
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            ADVANCED <span className="bg-gradient-to-r from-emerald-500 to-teal-500 bg-clip-text text-transparent">DIGITAL ENGINES</span>
                        </h1>
                        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            We engineer custom software that gives your business a definitive edge.
                        </p>
                        <div className="p-5 bg-white/5 border border-white/10 rounded-xl backdrop-blur-xl max-w-md mx-auto border-l-4 border-l-emerald-500 text-left">
                            <pre className="font-mono text-emerald-400 text-[10px]">
                                <code>{`while(business_needs > tech) {
  create_innovation();
  optimize();
}`}</code>
                            </pre>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Capabilities Section */}
            <section className="py-12 px-6 bg-slate-900/40">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {offerings.map((item, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-slate-900 border border-white/5 rounded-2xl hover:border-emerald-500/30 transition-all group"
                            >
                                <item.icon size={26} className="text-emerald-500 mb-4 group-hover:rotate-12 transition-transform" />
                                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                                <p className="text-slate-400 leading-relaxed text-[11px]">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Tech Stack Visual */}
            <section className="py-12 px-6 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-black mb-6 leading-tight">
                                Architected for <span className="text-emerald-500">Scale</span>
                            </h2>
                            <div className="space-y-4">
                                <div className="flex gap-3 p-4 bg-white/5 border border-white/5 rounded-xl">
                                    <Cpu className="text-emerald-500 shrink-0" size={20} />
                                    <div>
                                        <h4 className="font-bold text-sm">Performance-First</h4>
                                        <p className="text-slate-400 text-xs">Optimized for speed and efficiency.</p>
                                    </div>
                                </div>
                                <div className="flex gap-3 p-4 bg-white/5 border border-white/5 rounded-xl">
                                    <Shield className="text-emerald-500 shrink-0" size={20} />
                                    <div>
                                        <h4 className="font-bold text-sm">Secured Delivery</h4>
                                        <p className="text-slate-400 text-xs">Deep security in every layer.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="md:w-1/2">
                            <img
                                src="https://images.pexels.com/photos/546819/pexels-photo-546819.jpeg"
                                alt="Architecture"
                                className="rounded-3xl border border-white/10 w-full h-48 object-cover shadow-2xl"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CustomSoftware;
